export const ASSET_ORIGIN = 'SYNTHETIC DATA / SIMULATED DEVICE / NON-OPERATIONAL';
export const ASSET_SCOPE = 'LOCAL_SYNTHETIC';
export const ASSET_LIMIT = 100;
export interface AssetUnknownReference {
  readonly status: 'UNKNOWN';
  readonly evidence: 'NOT_ESTABLISHED';
}
export interface ProviderDefinitionReference {
  readonly capabilityId: string;
  readonly definitionVersion: number;
  readonly definitionDigest: string;
}
export interface AssetProvider {
  readonly providerId: string;
  readonly displayName: string;
  readonly providerVersion: number;
  readonly originLabel: typeof ASSET_ORIGIN;
  readonly definitionReferences: readonly ProviderDefinitionReference[];
}
export interface LocalAssetSnapshot {
  readonly assetId: string;
  readonly displayName: string;
  readonly assetVersion: number;
  readonly originLabel: typeof ASSET_ORIGIN;
  readonly provider: AssetProvider;
  readonly assetCapabilityStatus: 'UNKNOWN';
  readonly assetCapabilityEvidence: 'NOT_ESTABLISHED';
  readonly organization: AssetUnknownReference;
  readonly productModel: AssetUnknownReference;
}
export interface LocalAssetCatalogQuery {
  readonly scope: typeof ASSET_SCOPE;
  readonly limit: typeof ASSET_LIMIT;
}
export interface LocalAssetCatalogResult {
  readonly status: 'SUCCESS' | 'SOURCE_UNAVAILABLE';
  readonly scope: typeof ASSET_SCOPE;
  readonly coverage: 'NOT_TRUNCATED' | 'TRUNCATED_AT_LIMIT' | 'UNKNOWN';
  readonly limit: typeof ASSET_LIMIT;
  readonly items: readonly LocalAssetSnapshot[] | null;
  readonly authority: 'NON_AUTHORITY';
}
export interface LocalAssetDetailResult {
  readonly status: 'AVAILABLE' | 'UNKNOWN' | 'UNAVAILABLE';
  readonly assetId: string;
  readonly snapshot: LocalAssetSnapshot | null;
  readonly authority: 'NON_AUTHORITY';
}
function object(value: unknown, keys: readonly string[]): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    throw new Error('Invalid asset contract');
  const result = value as Record<string, unknown>;
  if (Object.keys(result).length !== keys.length || keys.some((key) => !Object.hasOwn(result, key)))
    throw new Error('Closed asset contract');
  return result;
}
function text(value: unknown): string {
  if (typeof value !== 'string' || value.trim().length === 0) throw new Error('Invalid text');
  return value;
}
function version(value: unknown): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 1)
    throw new Error('Invalid safe version');
  return value;
}
function origin(value: unknown): typeof ASSET_ORIGIN {
  if (value !== ASSET_ORIGIN) throw new Error('Invalid origin');
  return ASSET_ORIGIN;
}
function unknownReference(value: unknown): AssetUnknownReference {
  const raw = object(value, ['status', 'evidence']);
  if (raw.status !== 'UNKNOWN' || raw.evidence !== 'NOT_ESTABLISHED')
    throw new Error('Unknown must remain unknown');
  return Object.freeze({ status: 'UNKNOWN', evidence: 'NOT_ESTABLISHED' });
}
export function assetSnapshot(value: unknown): LocalAssetSnapshot {
  const raw = object(value, [
    'assetId',
    'displayName',
    'assetVersion',
    'originLabel',
    'provider',
    'assetCapabilityStatus',
    'assetCapabilityEvidence',
    'organization',
    'productModel',
  ]);
  const provider = object(raw.provider, [
    'providerId',
    'displayName',
    'providerVersion',
    'originLabel',
    'definitionReferences',
  ]);
  if (!Array.isArray(provider.definitionReferences)) throw new Error('Invalid references');
  const ids = new Set<string>();
  const references = provider.definitionReferences.map((value) => {
    const ref = object(value, ['capabilityId', 'definitionVersion', 'definitionDigest']);
    const capabilityId = text(ref.capabilityId);
    if (ids.has(capabilityId)) throw new Error('Duplicate definition identity');
    ids.add(capabilityId);
    return Object.freeze({
      capabilityId,
      definitionVersion: version(ref.definitionVersion),
      definitionDigest: text(ref.definitionDigest),
    });
  });
  if (raw.assetCapabilityStatus !== 'UNKNOWN' || raw.assetCapabilityEvidence !== 'NOT_ESTABLISHED')
    throw new Error('No asset capability inference');
  return Object.freeze({
    assetId: text(raw.assetId),
    displayName: text(raw.displayName),
    assetVersion: version(raw.assetVersion),
    originLabel: origin(raw.originLabel),
    provider: Object.freeze({
      providerId: text(provider.providerId),
      displayName: text(provider.displayName),
      providerVersion: version(provider.providerVersion),
      originLabel: origin(provider.originLabel),
      definitionReferences: Object.freeze(references),
    }),
    assetCapabilityStatus: 'UNKNOWN',
    assetCapabilityEvidence: 'NOT_ESTABLISHED',
    organization: unknownReference(raw.organization),
    productModel: unknownReference(raw.productModel),
  });
}
export function assetCatalogQuery(): LocalAssetCatalogQuery {
  return Object.freeze({ scope: ASSET_SCOPE, limit: ASSET_LIMIT });
}
export function assetCatalogResult(value: unknown): LocalAssetCatalogResult {
  const raw = object(value, ['status', 'scope', 'coverage', 'limit', 'items', 'authority']);
  if (raw.scope !== ASSET_SCOPE || raw.limit !== ASSET_LIMIT || raw.authority !== 'NON_AUTHORITY')
    throw new Error('Fixed asset scope');
  if (raw.status === 'SOURCE_UNAVAILABLE') {
    if (raw.items !== null || raw.coverage !== 'UNKNOWN')
      throw new Error('Unavailable is not empty');
    return Object.freeze({
      status: 'SOURCE_UNAVAILABLE',
      scope: ASSET_SCOPE,
      coverage: 'UNKNOWN',
      limit: ASSET_LIMIT,
      items: null,
      authority: 'NON_AUTHORITY',
    });
  }
  if (
    raw.status !== 'SUCCESS' ||
    !Array.isArray(raw.items) ||
    (raw.coverage !== 'NOT_TRUNCATED' && raw.coverage !== 'TRUNCATED_AT_LIMIT') ||
    raw.items.length > ASSET_LIMIT ||
    (raw.coverage === 'TRUNCATED_AT_LIMIT' && raw.items.length !== ASSET_LIMIT)
  )
    throw new Error('Invalid catalog combination');
  const items = raw.items.map(assetSnapshot);
  for (let i = 1; i < items.length; i++)
    if (items[i - 1]!.assetId >= items[i]!.assetId)
      throw new Error('Unique raw ascending IDs required');
  return Object.freeze({
    status: 'SUCCESS',
    scope: ASSET_SCOPE,
    coverage: raw.coverage as 'NOT_TRUNCATED' | 'TRUNCATED_AT_LIMIT',
    limit: ASSET_LIMIT,
    items: Object.freeze(items),
    authority: 'NON_AUTHORITY',
  });
}
export function assetDetailResult(value: unknown, expectedId?: string): LocalAssetDetailResult {
  const raw = object(value, ['status', 'assetId', 'snapshot', 'authority']);
  const assetId = text(raw.assetId);
  if (raw.authority !== 'NON_AUTHORITY' || (expectedId !== undefined && assetId !== expectedId))
    throw new Error('Detail identity or authority mismatch');
  if (raw.status === 'AVAILABLE') {
    const snapshot = assetSnapshot(raw.snapshot);
    if (snapshot.assetId !== assetId) throw new Error('Wrong asset snapshot');
    return Object.freeze({ status: 'AVAILABLE', assetId, snapshot, authority: 'NON_AUTHORITY' });
  }
  if ((raw.status !== 'UNKNOWN' && raw.status !== 'UNAVAILABLE') || raw.snapshot !== null)
    throw new Error('Invalid detail combination');
  return Object.freeze({
    status: raw.status as 'UNKNOWN' | 'UNAVAILABLE',
    assetId,
    snapshot: null,
    authority: 'NON_AUTHORITY',
  });
}
export function filterAssets(
  items: readonly LocalAssetSnapshot[],
  text: string,
): readonly LocalAssetSnapshot[] {
  const query = text.trim().toLocaleLowerCase();
  return items.filter(
    (item) =>
      item.assetId.toLocaleLowerCase().includes(query) ||
      item.displayName.toLocaleLowerCase().includes(query),
  );
}
